import { useState } from "react";
import type { ChangeEvent } from "react";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdekgkpl";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const fieldName = name as keyof FormErrors;
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: undefined,
      }));
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error("Formspree responded with an error");
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(
        "Something went wrong sending your message. Please try again, or email us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = (): void => {
    setIsSubmitted(false);
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
    });
    setErrors({});
  };

  if (isSubmitted) {
    return (
      <div className="bsp-center" style={{ marginTop: 40 }}>
        <div style={{ textAlign: "center", maxWidth: 500, margin: "0 auto" }}>
          <div
            style={{
              width: 64,
              height: 64,
              backgroundColor: "#3b82f6",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              style={{ width: 32, height: 32, color: "white" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="bsp-h2 font_family_base">Thank You!</h2>
          <p className="bsp-body font_family_base" style={{ marginBottom: 30 }}>
            Your message has been sent successfully. We&apos;ll get back to
            you soon!
          </p>
          <button
            onClick={resetForm}
            className="bsp-btn bsp-btn-outline font_family_base"
            style={{ border: "2px solid rgba(255,255,255,0.35)" }}
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520, width: "100%", margin: "0 auto" }}>
      <div className="bsp-form-field font_family_base">
        <label htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Your full name"
          style={{
            borderColor: errors.name ? "#fa5252" : undefined,
          }}
        />
        {errors.name && <p className="bsp-form-error">{errors.name}</p>}
      </div>

      <div className="bsp-form-field font_family_base">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your.email@example.com"
          style={{
            borderColor: errors.email ? "#fa5252" : undefined,
          }}
        />
        {errors.email && <p className="bsp-form-error">{errors.email}</p>}
      </div>

      <div className="bsp-form-field font_family_base">
        <label htmlFor="subject">Subject *</label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          placeholder="What's this about?"
          style={{
            borderColor: errors.subject ? "#fa5252" : undefined,
          }}
        />
        {errors.subject && <p className="bsp-form-error">{errors.subject}</p>}
      </div>

      <div className="bsp-form-field font_family_base">
        <label htmlFor="message">Message *</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={5}
          placeholder="Tell us what's on your mind..."
          style={{
            borderColor: errors.message ? "#fa5252" : undefined,
            resize: "vertical",
            minHeight: 120,
          }}
        />
        {errors.message && <p className="bsp-form-error">{errors.message}</p>}
      </div>

      {submitError && (
        <p
          className="bsp-form-error bsp-center font_family_base"
          style={{ marginTop: 10 }}
        >
          {submitError}
        </p>
      )}

      <div className="bsp-center" style={{ marginTop: 30 }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bsp-btn bsp-btn-primary font_family_base"
          style={{
            width: "100%",
            opacity: isSubmitting ? 0.7 : 1,
            cursor: isSubmitting ? "not-allowed" : "pointer",
          }}
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </button>
      </div>
    </div>
  );
}
